import { openDB } from 'idb';

const DB_NAME = 'quietroom';
const DB_VERSION = 2; // Bumped for version history

let dbInstance = null;

export const initDB = async () => {
    if (dbInstance) return dbInstance;
    
    dbInstance = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion) {
            // Ideas store
            if (!db.objectStoreNames.contains('ideas')) {
                const ideaStore = db.createObjectStore('ideas', { keyPath: 'id' });
                ideaStore.createIndex('updatedAt', 'updatedAt');
                ideaStore.createIndex('createdAt', 'createdAt');
            }
            
            // Settings store
            if (!db.objectStoreNames.contains('settings')) {
                db.createObjectStore('settings', { keyPath: 'key' });
            }
            
            // Snapshots store for backups
            if (!db.objectStoreNames.contains('snapshots')) {
                const snapshotStore = db.createObjectStore('snapshots', { keyPath: 'id' });
                snapshotStore.createIndex('timestamp', 'timestamp');
            }
            
            // API Keys store (encrypted, write-only semantics enforced in app layer)
            if (!db.objectStoreNames.contains('apiKeys')) {
                db.createObjectStore('apiKeys', { keyPath: 'provider' });
            }
            
            // Version history store (new in v2)
            if (!db.objectStoreNames.contains('versions')) {
                const versionStore = db.createObjectStore('versions', { keyPath: 'id' });
                versionStore.createIndex('ideaId', 'ideaId');
                versionStore.createIndex('timestamp', 'timestamp');
                versionStore.createIndex('ideaId_timestamp', ['ideaId', 'timestamp']);
            }
        }
    });
    
    return dbInstance;
};

// ============== Ideas CRUD ==============

export const createIdea = async (idea) => {
    const db = await initDB();
    const now = new Date().toISOString();
    const newIdea = {
        ...idea,
        createdAt: now,
        updatedAt: now,
    };
    await db.put('ideas', newIdea);
    return newIdea;
};

export const getIdea = async (id) => {
    const db = await initDB();
    return db.get('ideas', id);
};

export const getAllIdeas = async () => {
    const db = await initDB();
    const ideas = await db.getAll('ideas');
    return ideas.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
};

export const updateIdea = async (id, updates) => {
    const db = await initDB();
    const existing = await db.get('ideas', id);
    if (!existing) throw new Error('Idea not found');
    
    const updatedIdea = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString(),
    };
    await db.put('ideas', updatedIdea);
    return updatedIdea;
};

export const deleteIdea = async (id) => {
    const db = await initDB();
    await db.delete('ideas', id);
    // Also delete associated versions
    const versions = await getVersionsForIdea(id);
    for (const version of versions) {
        await db.delete('versions', version.id);
    }
};

export const duplicateIdea = async (id) => {
    const db = await initDB();
    const original = await db.get('ideas', id);
    if (!original) throw new Error('Idea not found');
    
    const { v4: uuidv4 } = await import('uuid');
    const now = new Date().toISOString();
    const duplicate = {
        ...original,
        id: uuidv4(),
        title: `${original.title} (copy)`,
        createdAt: now,
        updatedAt: now,
    };
    await db.put('ideas', duplicate);
    return duplicate;
};

// ============== Settings ==============

export const getSetting = async (key) => {
    const db = await initDB();
    const setting = await db.get('settings', key);
    return setting?.value;
};

export const setSetting = async (key, value) => {
    const db = await initDB();
    await db.put('settings', { key, value });
};

export const getAllSettings = async () => {
    const db = await initDB();
    const settings = await db.getAll('settings');
    return settings.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
    }, {});
};

// ============== API Keys (BYOK) ==============

export const saveApiKey = async (provider, encryptedKey, maskedKey) => {
    const db = await initDB();
    await db.put('apiKeys', {
        provider,
        encryptedKey,
        maskedKey,
        savedAt: new Date().toISOString(),
    });
};

export const getApiKey = async (provider) => {
    const db = await initDB();
    return db.get('apiKeys', provider);
};

export const getAllApiKeys = async () => {
    const db = await initDB();
    return db.getAll('apiKeys');
};

export const deleteApiKey = async (provider) => {
    const db = await initDB();
    await db.delete('apiKeys', provider);
};

export const hasApiKey = async (provider) => {
    const key = await getApiKey(provider);
    return !!key?.encryptedKey;
};

// ============== Version History ==============

export const createVersion = async (ideaId, title, content) => {
    const db = await initDB();
    const { v4: uuidv4 } = await import('uuid');
    
    const version = {
        id: uuidv4(),
        ideaId,
        title,
        content,
        timestamp: new Date().toISOString(),
    };
    
    await db.put('versions', version);
    
    // Keep only last 50 versions per idea to prevent bloat
    const allVersions = await getVersionsForIdea(ideaId);
    if (allVersions.length > 50) {
        const toDelete = allVersions.slice(50);
        for (const v of toDelete) {
            await db.delete('versions', v.id);
        }
    }
    
    return version;
};

export const getVersionsForIdea = async (ideaId) => {
    const db = await initDB();
    const index = db.transaction('versions').store.index('ideaId');
    const versions = await index.getAll(ideaId);
    return versions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

export const getVersion = async (versionId) => {
    const db = await initDB();
    return db.get('versions', versionId);
};

export const deleteVersion = async (versionId) => {
    const db = await initDB();
    await db.delete('versions', versionId);
};

// ============== Export/Import ==============

export const exportAllIdeas = async () => {
    const db = await initDB();
    const ideas = await db.getAll('ideas');
    return ideas;
};

export const importIdeas = async (ideas) => {
    const db = await initDB();
    const tx = db.transaction('ideas', 'readwrite');
    for (const idea of ideas) {
        await tx.store.put(idea);
    }
    await tx.done;
};

export const clearAllData = async () => {
    const db = await initDB();
    await db.clear('ideas');
    await db.clear('settings');
    await db.clear('snapshots');
    await db.clear('apiKeys');
    await db.clear('versions');
};

// ============== First Time User ==============

export const isFirstTimeUser = async () => {
    const hasSeenOnboarding = await getSetting('hasSeenOnboarding');
    return !hasSeenOnboarding;
};

export const markOnboardingSeen = async () => {
    await setSetting('hasSeenOnboarding', true);
};
